/* eslint-disable indent */
import { history } from 'umi';
import get from 'lodash/get';
import sha256 from 'sha256';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import qs from 'qs';
import { appName, DEBUG_FLAG } from '../../config/defaultSettings';

export function getBasePath() {
  return `/${appName}`;
}

// 存储需要按路径区分的localStorage，如Auth-Token、userInfo
export function getItemPath(key, path) {
  const basePath = path || getBasePath();
  return `${basePath}:${key}`;
}

export function setLocalStorageWithPath(key, value, path) {
  const itemKey = getItemPath(key, path);
  window.localStorage.setItem(itemKey, value);
}

export function getLocalStorageWithPath(key, path) {
  const itemKey = getItemPath(key, path);
  return window.localStorage.getItem(itemKey);
}

export function removeLocalStorageWithPath(key, path) {
  const itemKey = getItemPath(key, path);
  return window.localStorage.removeItem(itemKey);
}

// 清空LocalStorage
export function clearLocalStorage() {
  localStorage.removeItem('Auth-Token');
  localStorage.removeItem('Refresh-Token');
  removeLocalStorageWithPath('Auth-Token', `/${appName}`);
  removeLocalStorageWithPath('userInfo', `/${appName}`);
  removeLocalStorageWithPath('subsInfo', `/${appName}`);
  removeLocalStorageWithPath('pwd_expired', `/${appName}`);
  removeLocalStorageWithPath('currentSubsInfo', `/${appName}`);
}

// 图片地址
export function getImgUrl(serverUrl, imageUrl) {
  if (imageUrl && imageUrl.includes('http')) {
    return imageUrl.replace(/\(/g, '%28').replace(/\)/g, '%29');
  }
  if (imageUrl) {
    const url = `${serverUrl}${imageUrl}`;
    return url.replace(/\(/g, '%28').replace(/\)/g, '%29');
  }
  return null;
}

// 应用内导航 - 支持多套 app bridge 集成
export function appUrlJump(targetUrl) {
  if (isEmpty(targetUrl)) return;

  const isWebUrl = targetUrl.includes('http');
  const routeConfig = { url: targetUrl };

  // 执行实际的导航逻辑
  // 仅当对应 bridge 真实存在且可调用时返回 true，否则返回 false 以便继续尝试下一个 bridge
  // 避免桥不存在时仍返回 true，导致降级的 window.location.href 被提前跳过
  const executeNavigation = mode => {
    switch (mode) {
      case 'dito': {
        const handler = window.dito?.[isWebUrl ? 'openNewWebview' : 'jumpVCWithUrl'];
        if (typeof handler !== 'function') return false;
        handler(targetUrl);
        return true;
      }
      case 'clp': {
        if (typeof window?.clp?.openAppUrl !== 'function') return false;
        routeConfig.schemeType = isWebUrl ? 3 : 1;
        window.clp.openAppUrl(JSON.stringify(routeConfig));
        return true;
      }
      case 'app': {
        if (typeof window.APP?.invokeClientMethod !== 'function') return false;
        window.APP.invokeClientMethod('openAppUrl', { url: targetUrl });
        return true;
      }
      case 'bridge': {
        // WebViewJavascriptBridge 是 connectJSBridge 同步回调的判定标志（见 document.ejs）
        // eslint-disable-next-line no-undef
        if (typeof connectJSBridge !== 'function' || !window.WebViewJavascriptBridge) return false;
        connectJSBridge(bridgeInstance => {
          const handler = isWebUrl ? 'openNewUrl' : 'openAppUrl';
          const payload = isWebUrl ? targetUrl : { pageUrl: targetUrl };
          bridgeInstance.callHandler(handler, payload);
        });
        return true;
      }
      default:
        return false;
    }
  };

  // 优先级顺序尝试不同的 bridge
  if (executeNavigation('dito')) return;
  if (executeNavigation('clp')) return;
  if (executeNavigation('app')) return;
  if (executeNavigation('bridge')) return;

  // 降级到标准浏览器导航
  window.location.href = targetUrl;
}

// 处理千分位数字
export const numberFormat = number => {
  // eslint-disable-next-line no-restricted-globals
  if (!number || isNaN(Number(number))) {
    return '0';
  }
  const num = Number(number);
  const str = String(number);

  // 检测小数位数，保留精度
  const decimalPlaces = str.includes('.') ? str.split('.')[1].length : 0;

  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

// 格式化时间
export const dateFormat = date => {
  let momentDate;
  if (moment.isMoment(date)) {
    momentDate = date;
  } else if (typeof date === 'string') {
    momentDate = moment(date);
  } else {
    return '-';
  }

  if (!momentDate.isValid()) {
    return '-';
  }

  return momentDate.format('YYYY-MM-DDTHH:mm:ss[Z]');
};

// app userInfo相关
export function storeAppUserInfo(userInfo, path) {
  const basePath = path || getBasePath();
  // const str = encrypt(JSON.stringify(userInfo));
  const str = JSON.stringify(userInfo);
  setLocalStorageWithPath('appInfo', str, basePath);
}

export function getAppUserInfo(path) {
  const basePath = path || getBasePath(); // 需要确认前缀
  const userInfo = getLocalStorageWithPath('appInfo', basePath);
  if (userInfo) {
    // userInfo = decrypt(userInfo);
    try {
      return JSON.parse(userInfo);
    } catch (err) {
      return {};
    }
  }
  return {};
}

export function clearAppUserInfo(path) {
  const basePath = path || getBasePath();
  removeLocalStorageWithPath('appInfo', basePath);
}

export const getAppInfo = callback => {
  // 初始化登录信息数据
  const initAppInfo = (appUserInfo = {}, isAppOpen) => {
    const urlParams = history.location.query || {};
    const token = appUserInfo['Auth-Token'] || appUserInfo?.token;
    // if (urlParams.product === 'ESHOP') {
    //   callback({});
    //   return;
    // }
    if (isAppOpen && isEmpty(token)) {
      // app内打开，且未登录状态，需要先登录
      window.dito?.goToLogin ? window.dito.goToLogin() : window.clp?.needLogin?.();
      callback({});
      return;
    }
    // 已登录, 整理数据结构
    const userInfo = get(appUserInfo, 'userInfo') || {};
    const { ditoNumber, accNbr } = userInfo;
    const initApp = {
      prefix: '',
      phone: accNbr,
      token,
      ...userInfo,
      ...appUserInfo,
    };
    if (!isEmpty(ditoNumber) && !isEmpty(accNbr)) {
      initApp.prefix = ditoNumber.substr(0, ditoNumber.indexOf(accNbr));
    }
    if (urlParams.product === 'ESHOP') {
      initApp.eshoptoken = initApp.token;
    }
    delete initApp.userInfo;
    window.localStorage.setItem('initApp', JSON.stringify(initApp));
    callback(initApp);
  };
  if (window.dito) {
    window.dito.hiddenNav();
    const appInfo = JSON.parse(window.dito.getUserInfo()) || {};
    initAppInfo(appInfo, true);
    return;
  }
  // eslint-disable-next-line no-alert
  // alert(33333, 111);
  if (window.clp) {
    window.clp.hiddenNavigationBar?.();
    const appInfo = JSON.parse(window?.clp?.getLoginInfo()) || {};
    initAppInfo(appInfo, true);
    return;
  }
  // if (window.APP) {
  //   const appInfo = JSON.parse(window?.APP?.getUserInfo()) || {};
  //   initAppInfo(appInfo, true);
  //   return;
  // }
  if (!window.dito && !window.clp) {
    // 非 APP 打开
    // debugger 是否开启
    if (DEBUG_FLAG) {
      const testUserInfo = {
        'Auth-Token': '',
        userInfo: {
          Email: '11@qq.com',
          accNbr: '9202204001',
          acctNbr: '246171',
          custId: 198738,
          custNbr: '1731002604450000',
          ditoNumber: '639202204001',
          isFWAFlag: 'N',
          paymentModeCd: '2100',
          subsId: 1133090,
          userId: 4109,
        },
      };
      initAppInfo(testUserInfo);
    } else {
      callback({});
    }
  }
};

export const getPlayParams = (data = {}) => {
  const initAppStr = window.localStorage.getItem('initApp');
  const initApp = get(JSON.parse(initAppStr), 'userInfo') || JSON.parse(initAppStr) || {};
  return {
    ...data,
    custId: initApp.custId,
    userId: initApp.userId,
    // attrMap: {
    //   EXP_CUST_ID: initApp.custId,
    // },
  };
};

// 此方法用于格式化时间字符串，然后将标准格式的时间字符串返回，并用于参数moment对象和date对象的时间计算对比
export const formatDateString = time => {
  if (isEmpty(time)) {
    return '';
  }
  if (time.includes('T') && time.includes('Z')) {
    return time.replace('T', ' ').replace('Z', '');
  }
  return time;
};

export const formatDate = (timeTemp, onlyDate) => {
  let time = timeTemp;
  if (isEmpty(time) || time === '--') {
    return '--';
  }
  let format = localStorage.getItem('timeFormate');
  if (time.includes('T') && time.includes('Z')) {
    time = time.replace('T', ' ').replace('Z', '');
    return moment(time).format(format);
  }
  if (onlyDate) {
    // 智能分离日期和时间部分，去除时间相关格式
    let cleaned = '';
    let idx = 0;
    while (idx < format.length) {
      const ch = format[idx];
      if (ch === 'H' || ch === 'h') {
        // 移除结果中紧邻的尾部空格（对应正则中 \s* 前导匹配）
        while (cleaned.length > 0 && cleaned[cleaned.length - 1] === ' ') {
          cleaned = cleaned.substring(0, cleaned.length - 1);
        }
        // 跳过 [Hh]+
        while (idx < format.length && (format[idx] === 'H' || format[idx] === 'h')) {
          idx += 1;
        }
        // 跳过 :?
        if (idx < format.length && format[idx] === ':') {
          idx += 1;
        }
        // 跳过 [m]*
        while (idx < format.length && format[idx] === 'm') {
          idx += 1;
        }
        // 跳过 :?
        if (idx < format.length && format[idx] === ':') {
          idx += 1;
        }
        // 跳过 [s]*
        while (idx < format.length && format[idx] === 's') {
          idx += 1;
        }
        // 跳过尾部空格（对应正则中 \s* 后续匹配）
        while (idx < format.length && format[idx] === ' ') {
          idx += 1;
        }
      } else {
        cleaned += ch;
        idx += 1;
      }
    }
    format = cleaned.trim();
  }
  // 处理空格分隔的日期格式（moment不直接支持）
  let result;
  if (onlyDate && /^\w+\s+\w+\s+\w+$/.test(format)) {
    // 对于 "DD MM YYYY" 这种格式，先用常规方式解析时间，再分别格式化每部分
    let momentObj = moment(time);

    // 如果解析失败，尝试常见格式
    if (!momentObj.isValid()) {
      const originalFormat = localStorage.getItem('timeFormate');
      const formats = [originalFormat, 'YYYY-MM-DD HH:mm:ss'].filter(Boolean);

      formats.forEach(fmt => {
        if (!momentObj.isValid()) {
          momentObj = moment(time, fmt);
        }
      });
    }

    if (momentObj.isValid()) {
      const parts = format.split(/\s+/);
      const formattedParts = parts.map(part => momentObj.format(part));
      result = formattedParts.join(' ');
    } else {
      result = '--';
    }
  } else {
    result = moment(time).format(format);
  }
  return result;
};

// 传参为/promotion/luckyDraw/getMyAchieveRewards返回接口数据
export const getDateAndDescription = record => {
  const { state, receiveDeadDate, stateDate, expDate, expiryDate, prcResExpDate } = record;
  const result = {
    date: '-',
    description: '',
  };
  // 未领取
  if (state === 'A') {
    if (receiveDeadDate) {
      result.description = 'Claim before:';
      result.date = formatDate(receiveDeadDate);
      return result;
    }
    result.description = 'Rewarded at:';
    result.date = formatDate(stateDate);
  } else if (state === 'B') {
    const expDateTemp = expDate || expiryDate || prcResExpDate;
    if (expDateTemp) {
      result.description =
        new Date(expDateTemp.replace('T', ' ').replace('Z', '')) < new Date()
          ? 'Usage expired:'
          : 'Valid until:';
      result.date = formatDate(expDateTemp);
      return result;
    }
    result.description = 'Claimed at:';
    result.date = formatDate(stateDate);
  } else {
    result.description = 'Claim expired:';
    result.date = formatDate(receiveDeadDate);
  }
  return result;
};

// 11/11/2023 at 14:30:00
export const formatDateWithAt = (timeTemp, onlyDate) => {
  let time = timeTemp;
  if (isEmpty(time)) {
    return '--';
  }
  time = time.replace('T', ' ').replace('Z', '');
  let format = localStorage.getItem('timeFormate');
  if (onlyDate) {
    // eslint-disable-next-line prefer-destructuring
    format = format.split(' ')[0];
  }
  const result = `${moment(time).format(format)} at ${moment(time).format('HH:mm:ss')}`;
  return result;
};

// 号码格式化
export const formatNumber = (str = '', length = 3) => {
  const strLen = `${str || ''}`.length;
  let newStr = null;
  for (let i = 0; i < strLen; i += length) {
    const res = str.substring(i, i + length);
    if (!newStr) {
      newStr = res;
    } else {
      newStr = `${newStr} ${res}`;
    }
  }
  return newStr || '';
};

export const onHistoryPush = (pathname, params, stateParams) => {
  // 1. 手动拆成 [path, search]
  const [cleanPathname, search = ''] = pathname.split('?');

  // 2. 把 search 解析成对象
  const builtinQuery = qs.parse(search, { ignoreQueryPrefix: true });
  const { query } = history.location;
  history.push({
    pathname: cleanPathname,
    query: {
      ...query,
      ...builtinQuery,
      ...(params || {}),
    },
    state: {
      ...(stateParams || {}),
    },
  });
};

export const onHistoryReplace = (pathname, params, stateParams) => {
  const { query } = history.location;
  history.replace({
    pathname,
    query: {
      ...query,
      ...(params || {}),
    },
    state: {
      ...(stateParams || {}),
    },
  });
};

// 获取设备类型
export function getPhoneType() {
  const ua = navigator.userAgent;
  const isWindowsPhone = /(?:Windows Phone)/.test(ua);
  const isSymbian = /(?:SymbianOS)/.test(ua) || isWindowsPhone;
  const isHarmony = /(?:HarmonyOS)/.test(ua);
  const isAndroid = /(?:Android)/.test(ua);
  const isFireFox = /(?:Firefox)/.test(ua);
  // const isChrome = /(?:Chrome|CriOS)/.test(ua);
  const isTablet =
    /(?:iPad|PlayBook)/.test(ua) ||
    (isAndroid && !/(?:Mobile)/.test(ua)) ||
    (isFireFox && /(?:Tablet)/.test(ua));
  const isPhone = /(?:iPhone)/.test(ua) && !isTablet;
  const isPc = !isPhone && !isAndroid && !isSymbian && !isHarmony;

  switch (true) {
    case isHarmony:
      return 'HarmonyOS';
    case isPhone:
      return 'ios';
    case isAndroid:
      return 'android';
    case isPc:
      return 'web';
    default:
  }
  return 'web';
}

export function goBack(fallbackCallback) {
  // 检测设备类型并执行相应的返回操作
  const detectAndNavigateBack = () => {
    if (!window.dito) return false;

    const platformInfo = (() => {
      try {
        const appData = window.localStorage.getItem('initApp');
        return JSON.parse(appData) || {};
      } catch {
        return {};
      }
    })();

    const isAndroid = platformInfo.platform === 'android' || getPhoneType() === 'android';

    const backMethod = isAndroid ? 'backHome' : 'backForwardPage';
    window.dito[backMethod]?.();
    return true;
  };

  const handler1 = () => detectAndNavigateBack();
  const handler2 = () => window.clp?.closePage?.() || false;
  const handler3 = () => {
    if (!window.WebViewJavascriptBridge) return false;
    // eslint-disable-next-line no-undef
    connectJSBridge(bridgeInstance => {
      bridgeInstance.callHandler('goBackHome');
    });
    return true;
  };

  if (handler1() || handler2() || handler3()) return;

  if (fallbackCallback) {
    fallbackCallback();
  } else {
    history.goBack();
  }
}

export function HexToRGB(color, opacity) {
  if (color === undefined || opacity === undefined) {
    return 'rgba(0, 0, 0, 1)';
  }
  let newColor = 'rgba(';
  // 判断是三位还是六位
  if (color.length === 4) {
    const arry = [];
    for (let i = 1; i < color.length; i += 1) {
      // eslint-disable-next-line radix
      arry.push(parseInt(`0x${color[i]}${color[i]}`));
    }
    arry.forEach(item => {
      newColor += `${item}, `;
    });
    newColor += `${opacity / 100})`;
    return newColor;
  }
  const arry = [];
  for (let i = 1; i < color.length; i += 2) {
    // eslint-disable-next-line radix
    arry.push(parseInt(`0x${color.slice(i, i + 2)}`));
  }
  arry.forEach(item => {
    newColor += `${item}, `;
  });
  newColor += `${opacity / 100})`;
  return newColor;
}

export const getEcareSignInfo = (options = {}) => {
  const { url = '', data = {}, params = {}, method = 'POST' } = options;

  const signInfo = {};

  const methodOption = method.toUpperCase();

  const authToken = get(options, 'headers.Token') || '';

  let signUrl = url.split('/ecare/')[1];

  if (!signUrl) {
    // uc 签名
    if (url.startsWith('/uc/')) {
      // eslint-disable-next-line prefer-destructuring
      signUrl = url.split('/uc/')[1];
      signUrl = `uc/${signUrl}`;
    }

    if (url.startsWith(window.clpWebConfig?.promitionBaseUrl)) {
      // eslint-disable-next-line prefer-destructuring
      signUrl = url.split(window.clpWebConfig?.promitionBaseUrl)[1];
      if (signUrl?.startsWith('/')) {
        signUrl = signUrl.substring(1);
      }
      signInfo.authtoken = !authToken ? '' : authToken; // gcp 签名使用
    }
  }

  if (signUrl) {
    if (methodOption === 'GET') {
      Object.keys(params).forEach((ele, index) => {
        signUrl = `${signUrl}${index === 0 ? '?' : '&'}${ele}=${params[ele]}`;
      });
      const signCode = `/${signUrl}`;
      const shaParams = `${signCode}${authToken}32BytesString`.replace(/null/g, '');
      // headers新增一个参数sign_code
      signInfo.signcode = sha256(shaParams);
    } else {
      let signCode = JSON.stringify(data);
      // 只保留数字和字母,剔除null
      signCode = signCode.replace(/[^a-zA-Z\d]/g, '');
      signCode = signCode.replace(/null/g, '');
      // headers新增一个参数sign_code
      signInfo.signcode = sha256(`/${signUrl}${signCode}${authToken}32BytesString`);
    }
  }
  // const deviceId = new Fingerprint().get();

  signInfo.Token = !authToken ? '' : authToken;
  signInfo['Device-Type'] = 'web';
  // signInfo['Device-Id'] = deviceId;
  signInfo.timestamp = new Date().getTime();
  return signInfo;
};

export const getSensorsDeviceType = opt => {
  let type = getPhoneType();
  type = type?.toLocaleLowerCase() || '';
  if (opt === 'lower') {
    return type.includes('harmonyos') ? 'harmony' : type;
  }
  if (type.includes('ios')) {
    return 'iOS';
  }
  if (type.includes('android')) {
    return 'Android';
  }
  if (type.includes('harmonyos')) {
    return 'HarmonyOS';
  }
  if (type.includes('web')) {
    return 'Web';
  }
  return 'Web';
};

/**
 * 获取品牌
 */
export const getBrandInfo = () => {
  const origin = window?.location?.origin;
  let defaultConfig = {};
  try {
    const storedConfig = window.sessionStorage.getItem('x-default-config');
    defaultConfig = storedConfig ? JSON.parse(storedConfig) : {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse session storage config:', error);
  }

  const brand = {
    GOMO: {
      RESOURCE_DOMAIN: defaultConfig?.RESOURCE_DOMAIN,
      API_DOMAIN: defaultConfig?.API_DOMAIN,
    },
    HEYA: {
      RESOURCE_DOMAIN: defaultConfig?.HEYA_RESOURCE_DOMAIN,
      API_DOMAIN: defaultConfig?.HEYA_API_DOMAIN,
    },
    HIAPP: {
      RESOURCE_DOMAIN: defaultConfig?.HIAPP_RESOURCE_DOMAIN,
      API_DOMAIN: defaultConfig?.HIAPP_API_DOMAIN,
    },
  };

  const brandKey = Object.keys(brand).find(key => brand[key]?.RESOURCE_DOMAIN === origin) || 'GOMO';
  return { brandKey, brand };
};

/**
 * 检测设备类型
 * @returns {'mobile' | 'tablet' | 'desktop'}
 */
export const getDeviceType = () => {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const width =
    window.screen?.width || window.innerWidth || document.documentElement.clientWidth || 0;

  const isAndroid = /Android/i.test(ua);
  const isIPhone = /iPhone|iPod/i.test(ua);
  const isIPad = /iPad/i.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);
  const isMobileUA = /Mobile/i.test(ua);
  const isPlayBook = /PlayBook/i.test(ua);

  // 平板判断：iPad、PlayBook、Android无Mobile且屏幕>=768px
  if (isIPad || isPlayBook || (isAndroid && !isMobileUA && maxTouchPoints > 1)) {
    return 'tablet';
  }

  // 手机判断：iPhone/iPod、Android有Mobile
  if (isIPhone || (isAndroid && isMobileUA)) {
    return 'mobile';
  }

  // 其他触摸设备根据屏幕尺寸和系统判断
  if (maxTouchPoints > 1) {
    // 大屏幕触摸设备，如果是桌面系统则认为是触摸屏电脑
    if (width >= 1024 && /Windows NT|Mac OS X|Linux/.test(ua)) {
      return 'desktop';
    }
    // 中等屏幕触摸设备
    if (width >= 768) return 'tablet';
    return 'mobile';
  }

  // 非触摸设备默认为桌面
  return 'desktop';
};

export const getSignInfo = (options = {}) => {
  const { url: urlTemp = '', data = {}, params = {}, method = 'POST' } = options;
  let url = urlTemp.replace('/promotion-rest-boot', '');

  const { brandKey, brand } = getBrandInfo();
  const currentBrand = brand[brandKey] || {};
  if (currentBrand.API_DOMAIN) {
    // 使用品牌的API_DOMAIN替换原始URL的域名部分
    url = url.replace(currentBrand.API_DOMAIN, '');
    // url = url.replace('https://api.uat1.test.aws.singtel.com', '');
  }

  let signInfo = {};
  let appInfo = {};
  if (window.clp) {
    appInfo = JSON.parse(window.clp?.getLoginInfo?.() || '{}');
  }
  const { token, deviceModel, version, osVersion, language, clientKey, deviceId, platform, os } =
    appInfo;
  const { product, userDimension, tenantId, userType } = history.location.query || {};

  const methodOption = method.toUpperCase();
  const initAppStr = window.localStorage.getItem('initApp');
  const initApp = JSON.parse(initAppStr) || {};
  const urlParams = history.location.query || {};
  const authToken =
    get(options, 'headers.Token') || token || initApp.token || urlParams.token || '';
  // if (url.includes('getConfig') || url.includes('qryConfigItemParamValueByCode')) {
  //   authToken = authToken || options.uuidToken;
  // }

  let signUrl = url.split('/ecare/')[1];
  const timestamp = new Date().getTime();
  let defaultConfig = {};
  try {
    defaultConfig = JSON.parse(window.sessionStorage.getItem('x-default-config')) || {};
  } catch {
    //
  }
  //  Math.floor(new Date().getTime() / 1000) * 1000;
  if (!signUrl) {
    // uc 签名
    if (url.startsWith('/uc/')) {
      // eslint-disable-next-line prefer-destructuring
      signUrl = url.split('/uc/')[1];
      signUrl = `uc/${signUrl}`;
    }
    if (url.startsWith('/dxp/')) {
      // eslint-disable-next-line prefer-destructuring
      signUrl = url.split('/dxp/')[1];
      signUrl = `dxp/${signUrl}`;
    }
    if (url.startsWith('/promotion/')) {
      // eslint-disable-next-line prefer-destructuring
      signUrl = url.split('/promotion/')[1];
      signUrl = `promotion/${signUrl}`;
    }
    if (defaultConfig?.PROXY_PATH_ENABLED) {
      if (url.startsWith('/api/sg/v1/')) {
        // eslint-disable-next-line prefer-destructuring
        signUrl = url.split('/api/sg/v1/')[1];
        if (signUrl?.startsWith('/')) {
          signUrl = signUrl.substring(1);
          signUrl = `api/sg/v1/${signUrl}`;
        } else {
          signUrl = `api/sg/v1/${signUrl}`;
        }
        signInfo.authtoken = authToken || '';
      }
    }
  }

  let xClientKey = clientKey;
  if (!xClientKey) {
    // xClientKey = 'GCP';
    // 目前front层不支持GCP，先临时恢复之前代码，默认cx
    xClientKey = 'cx';
    // const { brandKey } = getBrandInfo();
    if (brandKey === 'HEYA') {
      xClientKey = 'HEYA_APPS';
    }
    if (brandKey === 'HIAPP') {
      xClientKey = 'HIAPP';
    }
  }
  if (signUrl) {
    if (methodOption === 'GET') {
      Object.keys(params).forEach((ele, index) => {
        let paramValue = params[ele];
        if (typeof paramValue === 'object' && paramValue !== null) {
          paramValue = JSON.stringify(paramValue);
        }
        if (defaultConfig?.PROXY_PATH_ENABLED) {
          signUrl = `${signUrl}${index === 0 ? '?' : '&'}${ele}=${encodeURIComponent(paramValue)}`;
        } else {
          signUrl = `${signUrl}${index === 0 ? '?' : '&'}${ele}=${paramValue}`;
        }
      });
      const signCode = `/${signUrl}`;
      const shaParams = `${signCode}${timestamp}${
        xClientKey || ''
      }${authToken}32BytesString`.replace(/null/g, '');
      // headers新增一个参数sign_code
      signInfo.timestamp = timestamp;
      signInfo.signcode = sha256(shaParams);
    } else {
      let signCode = JSON.stringify(data);
      // 只保留数字和字母,剔除null
      signCode = signCode.replace(/[^a-zA-Z\d]/g, '');
      signCode = signCode.replace(/null/g, '');
      // headers新增一个参数sign_code
      const shaParams = `/${signUrl}${signCode}${timestamp}${
        xClientKey || ''
      }${authToken}32BytesString`;
      signInfo.timestamp = timestamp;
      signInfo.signcode = sha256(shaParams);
    }
  }

  let sgAssessToken = {};
  const sgToken = sessionStorage.getItem('sg-access-token');
  if (sgToken) {
    sgAssessToken = {
      Authorization: `Bearer ${sgToken}`,
    };
  }

  signInfo = {
    ...signInfo,
    ...sgAssessToken,
    Token: token || authToken || '',
    authtoken: token || authToken || '',
    'X-Device-Type': getDeviceType(),
    'Device-Type': getPhoneType(),
    'Device-Id': deviceId || appInfo?.deviceId || '',
    'Device-Model': deviceModel || '',
    Os: platform || os || '', // ios没有返回platform字段，使用os字段
    'Os-Version': osVersion || '',
    'App-Version': version || '',
    'Accept-Language': language || '',
    'X-Client-Key': xClientKey || '',
    product: product || tenantId || initApp.product || '0',
    userDimension: userDimension || userType || initApp.userDimension || '1',
  };

  // const url
  if (url.includes('/api/sg/v2/oauth/token')) {
    return {
      ...signInfo,
      'Content-Type': 'application/x-www-form-urlencoded',
    };
  }

  return signInfo;
};

export function HexToRGBByCC(color, opacity) {
  if (color === undefined || opacity === undefined) {
    return 'rgba(0, 0, 0, 1)';
  }
  let newColor = 'rgba(';
  // 判断是三位还是六位
  if (color.length === 4) {
    const arry = [];
    for (let i = 1; i < color.length; i += 1) {
      // eslint-disable-next-line radix
      arry.push(parseInt(`0x${color[i]}${color[i]}`));
    }
    arry.forEach(item => {
      newColor += `${item}, `;
    });
    newColor += `${opacity / 100})`;
    return newColor;
  }
  const arry = [];
  for (let i = 1; i < color.length; i += 2) {
    // eslint-disable-next-line radix
    arry.push(parseInt(`0x${color.slice(i, i + 2)}`));
  }
  arry.forEach(item => {
    newColor += `${item}, `;
  });
  newColor += `${opacity / 100})`;
  return newColor;
}

export function buildTree(containerList) {
  const map = {};
  const roots = [];

  // 将所有节点放入一个映射中
  (containerList || []).forEach(container => {
    map[container.containerId] = { ...container, children: [] };
  });

  // 构建树结构
  (containerList || []).forEach(container => {
    const node = map[container.containerId];
    if (container.parentId) {
      // 如果有父节点，则将该节点添加到父节点的children中
      map[container.parentId].children.push(node);
    } else {
      // 如果没有父节点，则该节点是根节点
      roots.push(node);
    }
  });

  return roots;
}

export const formatDateNew = (date, isSpecificTime) => {
  const dateTemp = formatDateString(date);
  if (!dateTemp) {
    return '';
  }
  // needTime为true时，格式化包含
  if (isSpecificTime) return moment(date, 'YYYY-MM-DD HH:mm:ss').format('MMM. D, YYYY - h:mm A');
  return formatDate(dateTemp);
};

export const getPXToRem = size => `${size / 100}rem`;

/**
 * 从URL中提取路径部分（不包含域名和协议）
 */
export const extractPathFromUrl = url => {
  // 如果URL不是绝对路径，直接返回
  if (!url.startsWith('http')) return url;

  // 解析URL，提取路径
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse URL:', url, error);
    return url;
  }
};

export const getThemeImgByKey = key => {
  const gcpThemeStr = window.localStorage.getItem('gcpTheme') || '{}';
  const gcpTheme = JSON.parse(gcpThemeStr) || {};
  const illustrationList = gcpTheme.illustrationList || [];
  return illustrationList.find(item => item.illustrationKey === key)?.url;
};

export const shouldShowImage = imageUrl => {
  if (!imageUrl) {
    return false;
  }
  if (!imageUrl.includes('http')) {
    return false;
  }
  return true;
};

export const getDate = (data, redeemData, isExp) => {
  let newData = isEmpty(data) ? redeemData : '';
  if (isEmpty(newData)) {
    newData = redeemData;
  }
  if (!isEmpty(data) && !isEmpty(redeemData)) {
    if (isExp) {
      const res = moment(redeemData).isBefore(moment(data));
      newData = res ? redeemData : data;
    } else {
      const res = moment(redeemData).isAfter(moment(data));
      newData = res ? redeemData : data;
    }
  }
  return formatDate(newData);
};

export const getRequestParams = requestOptions => {
  const { method, data } = requestOptions;

  if (!method) return '';

  const upperMethod = method.toUpperCase();

  if (upperMethod === 'GET') {
    // GET请求：从URL中提取查询参数
    const url = new URL(requestOptions.url || '');
    return url.search ? url.search.substring(1) : ''; // 去掉开头的'?'
  }
  if (upperMethod === 'POST' || upperMethod === 'PUT' || upperMethod === 'PATCH') {
    // POST/PUT/PATCH请求：返回JSON字符串
    try {
      if (typeof data === 'string') {
        return data;
      }
      if (data) {
        return JSON.stringify(data);
      }
    } catch (e) {
      return '';
    }
  }

  return '';
};


// 手机号按指定位数分段
export const formatPhoneSegment = (phone, num = 4) => {
  if (!phone) {
    return '';
  }
  const n = Number(num) > 0 ? Number(num) : 4;
  const reg = new RegExp(`(.{${n}})(?=.)`, 'g');
  return String(phone).replace(reg, '$1 ');
};
